import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ClipboardList, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { fetcher } from "@/lib/api";
import { ORDER_STATUS, formatRange, formatShortDate, rupiah } from "@/lib/format";
import { PageLoader, StatusBadge } from "@/components/common";
import { PageHeader, StatCard } from "@/components/admin/ui";
import { IncomeChart } from "@/components/admin/IncomeChart";

const Card = ({ title, link, children, testId }) => (
  <div className="panel p-5 sm:p-6" data-testid={testId}>
    <div className="mb-5 flex items-center justify-between">
      <h3 className="font-display text-xl">{title}</h3>
      {link && <Link to={link.to} className="text-xs text-neutral-500 hover:text-gold">{link.label}</Link>}
    </div>
    {children}
  </div>
);

const Upcoming = ({ orders }) => (
  <Card title="Trip terdekat" link={{ to: "/admin/jadwal", label: "Lihat jadwal" }} testId="admin-upcoming">
    {orders.length === 0 && <p className="text-sm text-neutral-500">Belum ada trip terjadwal.</p>}
    <ul className="divide-y divide-white/5">
      {orders.map((o) => (
        <li key={o.id} className="flex items-center justify-between gap-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm">{o.trip_name} · <span className="text-neutral-500">{o.package_name}</span></p>
            <p className="text-xs text-neutral-500">{formatRange(o.start_date, o.end_date)} · {o.customer_name} · {o.participants} pax</p>
          </div>
          <StatusBadge status={o.status} />
        </li>
      ))}
    </ul>
  </Card>
);

const Recent = ({ items }) => (
  <Card title="Transaksi terakhir" link={{ to: "/admin/keuangan", label: "Lihat semua" }} testId="admin-recent-transactions">
    {items.length === 0 && <p className="text-sm text-neutral-500">Belum ada transaksi.</p>}
    <ul className="divide-y divide-white/5">
      {items.map((t) => (
        <li key={t.id} className="flex items-center justify-between gap-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm">{t.category}</p>
            <p className="truncate text-xs text-neutral-500">{formatShortDate(t.date)} · {t.description || "-"}</p>
          </div>
          <span className={`whitespace-nowrap font-mono text-sm ${t.type === "pemasukan" ? "text-emerald-300" : "text-rose-300"}`}>
            {t.type === "pemasukan" ? "+" : "−"}{rupiah(t.amount)}
          </span>
        </li>
      ))}
    </ul>
  </Card>
);

export default function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-stats"], queryFn: () => fetcher("/admin/stats") });
  if (isLoading || !data) return <PageLoader />;
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <PageHeader title="Dashboard" subtitle="Ringkasan pesanan dan keuangan" />
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-5">
        <StatCard label="Pemasukan" value={rupiah(data.income_total)} icon={TrendingUp} tone="gold" hint={`Bulan ini ${rupiah(data.month_income)}`} testId="admin-stat-income" />
        <StatCard label="Pengeluaran" value={rupiah(data.expense_total)} icon={TrendingDown} tone="rose" hint={`Bulan ini ${rupiah(data.month_expense)}`} testId="admin-stat-expense" />
        <StatCard label="Laba bersih" value={rupiah(data.profit)} icon={Wallet} tone={data.profit >= 0 ? "emerald" : "rose"} testId="admin-stat-profit" />
        <StatCard label="Pesanan" value={data.orders_total} icon={ClipboardList} hint={`${data.by_status.menunggu} menunggu pembayaran`} testId="admin-stat-orders" />
        <StatCard label="Belum dibayar" value={rupiah(data.outstanding)} icon={AlertCircle} tone="amber" hint="Sisa tagihan pesanan aktif" testId="admin-stat-outstanding" />
      </div>
      <div className="grid gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2"><Card title="Pemasukan vs pengeluaran (6 bulan)"><IncomeChart data={data.monthly} /></Card></div>
        <Card title="Status pesanan" link={{ to: "/admin/pesanan", label: "Kelola" }} testId="admin-status-breakdown">
          <ul className="space-y-3">
            {Object.entries(ORDER_STATUS).map(([key]) => (
              <li key={key} className="flex items-center justify-between">
                <StatusBadge status={key} />
                <span className="font-mono text-lg" data-testid={`admin-status-count-${key}`}>{data.by_status[key] || 0}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <Upcoming orders={data.upcoming} />
        <Recent items={data.recent_transactions} />
      </div>
    </div>
  );
}
